import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { ToastViewer } from '../../../../components/common/text-viewer/ToastViewer';
import { YoutubeSection } from '../../../../components/common/video/YoutubeSection';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { IInformation } from '../../../../interfaces/Information.interfaces';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { formatDate } from '../../../../utils/formatter/formatDate.util';
import { InfoVerticalListView } from '../../listView/InfoVerticalListView/InfoVerticalListView';

interface InfoDetailViewProps {
  information: IInformation;
  informationNeighbors?: IInformation[];
}

export const InfoDetailView = ({ information, informationNeighbors }: InfoDetailViewProps) => {
  const navigate = useNavigate();
  const Popup = usePopup();

  const handleShareButtonClick = () => {
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'share', messageParam: window.location.href });
    } else {
      window.navigator.clipboard.writeText(window.location.href);
      Popup.toast('현재 게시물의 주소가 복사되었습니다.');
    }
  };

  return (
    <Container>
      <HeaderSection>
        <TitleSection>
          <Title>{information.title}</Title>
          <PreviewContent>{information.previewContent}</PreviewContent>
        </TitleSection>

        <ImageWrapper>
          <Image src={information.thumbnailUrl} />
        </ImageWrapper>

        <MetaSection>
          {information.category === 'NEWS' && <NewsCategory>{'뉴스'}</NewsCategory>}
          {information.category === 'MAGAZINE' && <MagazineCategory>{'매거진'}</MagazineCategory>}

          <SplitBar>{'|'}</SplitBar>

          <MetaDataText>{formatDate(information.createdAt)}</MetaDataText>

          <SplitBar>{'|'}</SplitBar>

          <MetaDataText>{`조회수 ${information.views.toLocaleString()}`}</MetaDataText>
        </MetaSection>
      </HeaderSection>

      <ToastViewer content={information.content} />

      {information.youtubeUrl && <YoutubeSection youtubeUrl={information.youtubeUrl} />}

      <ButtonSection>
        <Button onClick={handleShareButtonClick}>
          <Icon name={'Link'} width={16} height={16} />
          <ButtonText>{'이 게시물 공유하기'}</ButtonText>
        </Button>
        <Button onClick={() => navigate('/info/list')}>
          <Icon name="Return" width={20} height={20} />
          <ButtonText>{'목록으로 돌아가기'}</ButtonText>
        </Button>
      </ButtonSection>

      <HorizontalLine />

      <PrevNextPostSection>
        <PrevNextPostTitle>{'다른 읽을 거리'}</PrevNextPostTitle>

        {informationNeighbors &&
          informationNeighbors.map((informationNeighbor) => <InfoVerticalListView information={informationNeighbor} />)}
      </PrevNextPostSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: 32px;

  padding: 24px var(--radius-large, 16px);

  background-color: #ffffff;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-medium, 8px);
`;

const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const PreviewContent = styled.h2`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-overflow: ellipsis;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ImageWrapper = styled.div`
  width: 100%;

  position: relative;

  padding-top: 75%;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;

  position: absolute;

  left: 0;
  top: 0;

  border-radius: var(--radius-medium, 8px);
`;

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  gap: 4px;
`;

const Category = styled.span`
  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const NewsCategory = styled(Category)`
  color: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const MagazineCategory = styled(Category)`
  color: var(--Light-Scheme-Color-primary-blue, #ff3049);
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey50, #babdca);

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;

const MetaDataText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-medium, 8px);
`;

const Button = styled.button`
  width: 100%;
  height: 56px;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px) 24px;

  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const HorizontalLine = styled.hr`
  width: 100%;

  height: 0px;
  align-self: stretch;
  stroke-width: 1px;
  stroke: #e4e4ed;
`;

const PrevNextPostSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: 24px;

  padding-bottom: 32px;
`;

const PrevNextPostTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;
